import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Button,
    Checkbox,
    Grid,
    InputAdornment,
    ListItemText,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import '../../style/SalesMarketing.css';
import * as Yup from 'yup';
import {useNavigate, useParams} from "react-router-dom";
import {ExpandMoreRounded, InfoRounded, LibraryAddRounded} from '@mui/icons-material';
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import Ancillaries from "../promo/sections/Ancillaries";
import {useProductCodeActions} from "../../service/useProductCodeActions";
import CouponCampaignCreationModal from "./modals/CouponCampaignCreationModal";
import {
    COUPON_CAMPAIGN_STATUS_ACTIVE,
    COUPON_CAMPAIGN_STATUS_DISMISSED,
    COUPON_CAMPAIGN_STATUS_DRAFT,
    CURRENCY_REG_EXP,
    INTEGER_REG_EXP
} from "../../component/Constants";
import dayjs from "dayjs";
import {checkValueWithInterval, floatToIntCurrency, intToFloatCurrency} from "../../component/Utils";
import {useCouponActions} from "../../service/useCouponActions";
import {useLookupActions} from "../../service/useLookupActions";
import {useStatusValuesCouponCampaign} from "../../component/useStatusValuesCouponCampaign";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import AdvancedDmsSelector from "../../component/AdvancedDmsSelector/AdvancedDmsSelector";
import SubPeriods from "../../component/SubPeriods";
import {
    preselectSingleEntity,
    UserLegalEntitiesOrDealersInputGroup
} from "../../component/UserLegalEntitiesOrDealersInputGroup/UserLegalEntitiesOrDealersInputGroup";

interface CouponCampaignDetail {
    tenantId: string,
    dealerId: string,
    id: number,
    productCode: string,
    status: string,
    prcRatePlan?: string,
    acrissCodeList?: string[],
    lmsList?: string[],
    dropDmsList?: string[],
    promoCode?: string,
    discountPercentage?: string,
    discountAmount?: string,
    bookingStartDate?: Date,
    bookingEndDate?: Date,
    pickupStartDate?: Date,
    pickupEndDate?: Date,
    description?: string,
    minimumDuration?: string,
    maxBookings?: string,
    maxUserBookings?: string,
    freeDays?: string,
    minimumDurationForFreeDays?: string,
    exclusionSubperiods?: any[]
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const CouponCampaignCreateOrUpdatePage = () => {
    const {globalData} = useContext(GlobalContext);
    const {couponCampaignId} = useParams();
    const isUpdate = !!couponCampaignId;
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'coupon'});
    const navigate = useNavigate();
    const [couponCampaign, setCouponCampaign] = useState<CouponCampaignDetail | null>(null);
    const [productCodes, setProductCodes] = useState<any>([]);
    const [productCodesMap, setProductCodesMap] = useState<any>();
    const [selectedProduct, setSelectedProduct] = useState<any>({
        startDate: null,
        endDate: null,
        name: "",
        description: ""
    });
    const [acrisCodes, setAcrisCodes] = useState<any>([]);
    const [lmsList, setLmsList] = useState<any>([]);
    const [dropDmsList, setDropDmsList] = useState<any>([]);
    const [ancillaryNameList, setAncillaryNameList] = useState<any>([]);
    const [ancillaryIndex, setAncillaryIndex] = useState(0);
    const [ancillaries, setAncillaries] = useState<any>([]);
    const [ancillariesErrorMessage, setAncillariesErrorMessage] = useState<any>();
    const {getCouponCampaignById, createCouponCampaign, updateCouponCampaign} = useCouponActions();
    const {getRatePlanInfoByProductCode} = useLookupActions();
    const {listProductCode} = useProductCodeActions();
    const {getStatusLabel} = useStatusValuesCouponCampaign();
    const [firstLoading, setFirstLoading] = useState(true);

    const [openCreationModal, setOpenCreationModal] = useState(false);

    const statusesDraft = [
        {
            key: COUPON_CAMPAIGN_STATUS_DRAFT,
            value: getStatusLabel(COUPON_CAMPAIGN_STATUS_DRAFT)
        },
        {
            key: COUPON_CAMPAIGN_STATUS_ACTIVE,
            value: getStatusLabel(COUPON_CAMPAIGN_STATUS_ACTIVE)
        }
    ]

    const statusesActive = [
        {
            key: COUPON_CAMPAIGN_STATUS_ACTIVE,
            value: getStatusLabel(COUPON_CAMPAIGN_STATUS_ACTIVE)
        },
        {
            key: COUPON_CAMPAIGN_STATUS_DISMISSED,
            value: getStatusLabel(COUPON_CAMPAIGN_STATUS_DISMISSED)
        }
    ]


    useEffect(() => {
        reset(initialValues);
    }, [couponCampaign])

    const initialValues = isUpdate && couponCampaign ?
        {
            tenantId: couponCampaign.tenantId ? couponCampaign.tenantId : '',
            dealerId: couponCampaign.dealerId ? couponCampaign.dealerId : '',
            id: couponCampaign.id ? couponCampaign.id : null,
            status: couponCampaign.status ? couponCampaign.status : "",
            productCode: couponCampaign.productCode ? couponCampaign.productCode : "",
            bookingStartDate: couponCampaign.bookingStartDate ? couponCampaign.bookingStartDate : null,
            bookingEndDate: couponCampaign.bookingEndDate ? couponCampaign.bookingEndDate : null,
            pickupStartDate: couponCampaign.pickupStartDate ? couponCampaign.pickupStartDate : null,
            pickupEndDate: couponCampaign.pickupEndDate ? couponCampaign.pickupEndDate : null,
            acrissCodeList: couponCampaign.acrissCodeList ? couponCampaign.acrissCodeList : [],
            lmsList: couponCampaign.lmsList ? couponCampaign.lmsList : [],
            dropDmsList: couponCampaign.dropDmsList ? couponCampaign.dropDmsList : [],
            description: couponCampaign.description ? couponCampaign.description : "",
            discountPercentage: couponCampaign.discountPercentage != null ? couponCampaign.discountPercentage : "",
            discountAmount: couponCampaign.discountAmount != null ? intToFloatCurrency(couponCampaign.discountAmount) : "",
            minimumDuration: couponCampaign.minimumDuration != null ? couponCampaign.minimumDuration : "",
            freeDays: couponCampaign.freeDays != null ? couponCampaign.freeDays : "",
            minimumDurationForFreeDays: couponCampaign.minimumDurationForFreeDays != null ? couponCampaign.minimumDurationForFreeDays : "",
            exclusionSubperiods: couponCampaign.exclusionSubperiods ? couponCampaign.exclusionSubperiods.map((sp: any) => {
                return {startDate: sp.startDate, endDate: sp.endDate}
            }) : []
        }
        :
        {
            tenantId: '',
            dealerId: '',
            status: COUPON_CAMPAIGN_STATUS_DRAFT,
            productCode: '',
            bookingStartDate: null,
            bookingEndDate: null,
            pickupStartDate: null,
            pickupEndDate: null,
            acrissCodeList: [],
            lmsList: [],
            dropDmsList: [],
            description: '',
            discountPercentage: '',
            discountAmount: '',
            minimumDuration: '',
            freeDays: '',
            minimumDurationForFreeDays: '',
            exclusionSubperiods: []
        };

    const validationSchema = Yup.object({
        status: Yup.string().required(t('createForm.requiredCheck')),
        productCode: Yup.string().required(t('createForm.requiredCheck')),
        bookingStartDate: Yup.date().required(t('createForm.requiredCheck')).nullable()
            .test("testBookingStartDateInsideProductDates", t('createForm.bookingStartDateCheck'), (value, context) => {
                return (value && selectedProduct.endDate && selectedProduct.startDate && value <= selectedProduct.endDate && value >= selectedProduct.startDate)
            }).test("testBookingStartDate", t('createForm.datesCheck'), (value, context) => {
                return (value && context.parent.bookingEndDate && value <= context.parent.bookingEndDate)
            })
            .typeError(t('createForm.invalidDate')),
        bookingEndDate: Yup.date().required(t('createForm.requiredCheck')).nullable()
            .test("testBookingEndDateInsideProductDates", t('createForm.bookingEndDateCheck'), (value, context) => {
                return (value && selectedProduct.endDate && selectedProduct.startDate && value <= selectedProduct.endDate && value >= selectedProduct.startDate)
            })
            .test("testBookingEndDate", t('createForm.datesCheck'), (value, context) => {
                return (value && context.parent.bookingStartDate && value >= context.parent.bookingStartDate)
            }).typeError(t('createForm.invalidDate')),
        pickupStartDate: Yup.date().required(t('createForm.requiredCheck')).nullable().test("testPickupStartDate", t('createForm.datesCheck'), (value, context) => {
            return (value && context.parent.pickupEndDate && value <= context.parent.pickupEndDate)
        }).typeError(t('createForm.invalidDate')),
        pickupEndDate: Yup.date().required(t('createForm.requiredCheck')).nullable().test("testPickupEndDate", t('createForm.datesCheck'), (value, context) => {
            return (value && context.parent.pickupStartDate && value >= context.parent.pickupStartDate)
        }).typeError(t('createForm.invalidDate')),
        acrissCodeList: Yup.array().min(1, t('createForm.requiredCheck')),
        lmsList: Yup.array().min(1, t('createForm.requiredCheck')),
        dropDmsList: Yup.array().min(1, t('createForm.requiredCheck')),
        description: Yup.string().nullable(),
        minimumDuration: Yup.string().nullable(),
        freeDays: Yup.string().nullable(),
        minimumDurationForFreeDays: Yup.string().nullable(),
        exclusionSubperiods: Yup.array()
            .of(
                Yup.object().shape({
                    startDate: Yup.date().nullable().required(t('createForm.requiredCheck')).test('a', t('createForm.dateRange'), (value, context) => {
                        return (!!value && !!context.parent.endDate) && value <= context.parent.endDate;
                    }).test('b', t('createForm.validSubperiod'), (value) => {
                        let startDate: any = watch('pickupStartDate');
                        return (!!value && !!startDate) && (dayjs(value).startOf('day') >= dayjs(startDate).startOf('day'));
                    }).typeError(t('createForm.invalidDate')),
                    endDate: Yup.date().nullable().required(t('createForm.requiredCheck')).test('a', t('createForm.dateRange'), (value, context) => {
                        return (!!value && !!context.parent.startDate) && value >= context.parent.endDate;
                    }).test('b', t('createForm.validSubperiod'), (value) => {
                        let endDate: any = watch('pickupEndDate');
                        return (!!value && !!endDate) && (dayjs(value).endOf('day') <= dayjs(endDate).endOf('day'));
                    }).typeError(t('createForm.invalidDate')),
                })
            )
            .nullable()
    });

    const onSubmit = async (values: any) => {

        if (values.discountAmount !== '') {
            values = {...values, discountAmount: floatToIntCurrency(values.discountAmount)}
        }

        let ancs = ancillaries.map((anc: any) => {
            return anc.amount ? {...anc, amount: floatToIntCurrency(anc.amount)} : anc
        });

        if (checkList()) {
            if (isUpdate) {
                let data = await updateCouponCampaign({...values}, ancs);
                if (data && data.status === 'OK') {
                    // show modal with confirm
                    setOpenCreationModal(true);
                } else {
                    // show snackbar with error
                }
            } else {
                if (globalData.user.legalEntities.length === 1) {
                    values = {...values, tenantId: globalData.user.legalEntities[0].name}
                }

                if (globalData.user.dealers.length === 1) {
                    values = {...values, dealerId: globalData.user.dealers[0].name}
                }
                let data = await createCouponCampaign({...values}, ancs);
                if (data && data.status === 'OK') {
                    // show modal with confirm
                    setOpenCreationModal(true);
                } else {
                    // show snackbar with error
                }
            }
        }
    }

    const {handleSubmit, control, setValue, watch, reset, formState} = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema)
    });

    const {fields, append, prepend, remove, swap, move, insert} = useFieldArray({
        control,
        name: "exclusionSubperiods"
    });

    const isAllLmsSelected =
        lmsList.length > 0 && watch("lmsList").length === lmsList.length;

    const isAllDropLmsSelected =
        dropDmsList.length > 0 && watch("dropDmsList").length === dropDmsList.length;

    const isAllAcrissSelected =
        acrisCodes.length > 0 && watch("acrissCodeList").length === acrisCodes.length;

    function checkList() {
        let result = {isOk: true};
        checkAncillaries(result);
        return result.isOk;
    }

    function checkAncillaries(check: any) {
        let copyErrMsg = {...ancillariesErrorMessage};
        ancillaries.forEach((s: any) => {
            if (s.name === "") {
                copyErrMsg["name" + s.id] = t('createForm.requiredCheck');
                check.isOk = false;
            }
            if (s.discount === "" && s.amount === "") {
                copyErrMsg["discount" + s.id] = t('createForm.requiredCheck');
                copyErrMsg["amount" + s.id] = t('createForm.requiredCheck');
                check.isOk = false
            }
        })
        setAncillariesErrorMessage(copyErrMsg);
    }

    useEffect(() => {
        if (watch("productCode") && productCodesMap.get(watch('productCode'))) {
            setSelectedProduct({
                startDate: dayjs(productCodesMap.get(watch("productCode")).startDate).startOf("day").toDate(),
                endDate: dayjs(productCodesMap.get(watch("productCode")).endDate).endOf("day").toDate(),
                name: productCodesMap.get(watch("productCode")).name,
                description: productCodesMap.get(watch("productCode")).description,
            })
            // retrieve lms, acris, ancillaries by productCode
            getRatePlanInfoByProductCode(watch("productCode")).then(res => {
                let locations = res.locationsExt;
                let carGroups = res.carGroups;
                let ancillarieNames = res.ancillaries;
                if (res && locations && carGroups && ancillarieNames) {
                    if (!isUpdate || !firstLoading) {
                        // Reset prev values
                        setValue("acrissCodeList", []);
                        setValue("lmsList", []);
                        setValue("dropDmsList", []);
                        setAncillaries([]);
                    }
                    setAcrisCodes(carGroups);
                    if (!isUpdate) {
                        // on creation select all items
                        setValue("acrissCodeList", carGroups);
                    }
                    setValue("acrissCodeList", carGroups);
                    setLmsList(locations);
                    setDropDmsList(locations);
                    setAncillaryNameList(ancillarieNames);
                    setFirstLoading(false);
                }
            })
        }
    }, [watch("productCode")]);

    function addAncillary() {
        setAncillaries([...ancillaries, {
            id: ancillaryIndex + 1,
            name: "",
            discount: "",
            amount: "",
            ida: "",
            code: ""
        }]);
        setAncillaryIndex(ancillaryIndex + 1);
    }

    useEffect(() => {
        setValue("productCode", '');
        setSelectedProduct({});
        setValue("acrissCodeList", []);
        setValue("lmsList", []);
        setValue("dropDmsList", []);
        setAncillaries([]);
        // Retrieve products
        listProductCode({offset: 0, limit: 10000}).then(response => {
            let products = response.content;
            if (products.length > 0) {
                setProductCodes(products
                    .filter((t:any) => t.tenantId === watch('tenantId') && t.dealerId === watch('dealerId'))
                    .map(p => p.productCode));
                let productsMap = new Map<string, any>();
                products.forEach(p => {
                    productsMap.set(p.productCode, {
                        startDate: p.startDate,
                        endDate: p.endDate,
                        name: p.name,
                        description: p.description
                    })
                })
                setProductCodesMap(productsMap);
                // Retrieve couponCampaign
                if (couponCampaignId && isUpdate) {
                    getCouponCampaignById(couponCampaignId).then(couponCampaignFound => {

                        setCouponCampaign(couponCampaignFound);
                        setSelectedProduct({
                            startDate: dayjs(productsMap.get(couponCampaignFound.productCode).startDate).startOf("day").toDate(),
                            endDate: dayjs(productsMap.get(couponCampaignFound.productCode).endDate).endOf("day").toDate(),
                            name: productsMap.get(couponCampaignFound.productCode).name,
                            description: productsMap.get(couponCampaignFound.productCode).description,
                        })
                        setAncillaries(couponCampaignFound?.ancillaryList.map((anc: any) => {
                            return anc.amount ? {...anc, amount: intToFloatCurrency(anc.amount)} : anc
                        }));
                        setAncillaryIndex(couponCampaignFound?.ancillaryList.length)
                    });
                }
            }
        });
    }, [watch('tenantId'), watch('dealerId')]);

    useEffect(() => {
        preselectSingleEntity(globalData, setValue);
    }, [globalData, setValue]);

    return (
        <>
            <SectionHeader
                title={t(isUpdate ? 'updateHeader.title' : 'createHeader.title')}
                subtitle={t('createHeader.subTitle')}/>
            <Paper elevation={3} sx={{padding: "16px", margin: "18px"}}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Grid container rowSpacing={3} columnSpacing={3}>
                    <UserLegalEntitiesOrDealersInputGroup
                                control={control}
                                disabled={isUpdate}
                            />
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="status"
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label={t('status')}
                                        disabled={!isUpdate}
                                        select
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    >
                                        {watch('status') === COUPON_CAMPAIGN_STATUS_DRAFT ?
                                            statusesDraft?.map((a) => (
                                                <MenuItem key={a.key} value={a.key}>
                                                    {a.value}
                                                </MenuItem>
                                            )) :
                                            statusesActive?.map((a) => (
                                                <MenuItem key={a.key} value={a.key}>
                                                    {a.value}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="productCode"
                                render={({field, fieldState}) => (
                                    <Autocomplete
                                        id="productCode"
                                        options={productCodes.sort().map((pc:string)=>{
                                            return `${pc} - ${productCodesMap.get(pc).name}`
                                        })}
                                        value={field.value}
                                        onChange={(e, newVal) => {
                                            let val = newVal ? newVal.split(' -')[0] : '' ;
                                            field.onChange(val)
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                disabled={watch("tenantId") === "" || watch("dealerId") === ""}
                                                label={t('productCode')}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={6}/>
                        <Grid item xs={3}>
                            <TextField
                                disabled
                                fullWidth
                                name="selectedProductName"
                                label={t('selectedProductName')}
                                value={selectedProduct.name ? selectedProduct.name : ""}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                disabled
                                fullWidth
                                name="selectedProductDescription"
                                label={t('selectedProductDescription')}
                                value={selectedProduct.description ? selectedProduct.description : ""}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                disabled
                                fullWidth
                                name="selectedProductStartDate"
                                label={t('selectedProductStartDate')}
                                value={selectedProduct.startDate ? dayjs(selectedProduct.startDate).format("L") : ""}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                disabled
                                fullWidth
                                name="selectedProductEndDate"
                                label={t('selectedProductEndDate')}
                                value={selectedProduct.endDate ? dayjs(selectedProduct.endDate).format("L") : ""}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="bookingStartDate"
                                render={({field, fieldState}) => (
                                    <DatePicker
                                        label={t('bookingStartDate')}
                                        value={field.value}
                                        onChange={(e) => {
                                            //field.onChange(e)
                                            setValue("bookingStartDate", dayjs(e).startOf("day").toDate());
                                        }}
                                        minDate={selectedProduct.startDate ? selectedProduct.startDate : null}
                                        maxDate={watch("bookingEndDate") ? watch("bookingEndDate") : null}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                required
                                                fullWidth
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />}
                                        inputFormat={"L"}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="bookingEndDate"
                                render={({field, fieldState}) => (
                                    <DatePicker
                                        label={t('bookingEndDate')}
                                        value={field.value}
                                        onChange={(e) => {
                                            // field.onChange(e)
                                            setValue("bookingEndDate", dayjs(e).startOf("day").toDate())
                                        }}
                                        minDate={watch("bookingStartDate") ? watch("bookingStartDate") : null}
                                        maxDate={selectedProduct.endDate ? selectedProduct.endDate : null}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                required
                                                fullWidth
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />}
                                        inputFormat={"L"}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="pickupStartDate"
                                render={({field, fieldState}) => (
                                    <DatePicker
                                        label={t('pickupStartDate')}
                                        value={field.value}
                                        onChange={(e: any) => {
                                            //  field.onChange(e)
                                            setValue("pickupStartDate", dayjs(e).startOf("day").toDate())
                                        }}
                                        maxDate={watch("pickupEndDate") ? watch("pickupEndDate") : null}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                required
                                                fullWidth
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />}
                                        inputFormat={"L"}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="pickupEndDate"
                                render={({field, fieldState}) => (
                                    <DatePicker
                                        label={t('pickupEndDate')}
                                        value={field.value}
                                        onChange={(e: any) => {
                                            // field.onChange(e)
                                            setValue("pickupEndDate", dayjs(e).startOf("day").toDate())
                                        }}
                                        minDate={watch("pickupStartDate") ? watch("pickupStartDate") : null}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                required
                                                fullWidth
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />}
                                        inputFormat={"L"}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="description"
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        label={t('description')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="discountPercentage"
                                render={({field, fieldState}) => (
                                    <TextField
                                        disabled={!!watch("discountAmount")}
                                        placeholder={watch("discountAmount") ? "--" : ""}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">%</InputAdornment>,
                                        }}
                                        label={t('discountPercentage')}
                                        value={field.value}
                                        onChange={(e) => {
                                            if (e.target.value === "" || INTEGER_REG_EXP.test(e.target.value)) {
                                                setValue("discountPercentage", checkValueWithInterval(e.target.value, 1, 100))
                                            }
                                        }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="discountAmount"
                                render={({field, fieldState}) => (
                                    <TextField
                                        disabled={!!watch("discountPercentage")}
                                        placeholder={watch("discountPercentage") ? "--" : ""}
                                        fullWidth
                                        label={t('discountAmount')}
                                        InputLabelProps={{shrink: true}}
                                        value={field.value}
                                        onChange={(e) => {
                                            if (e.target.value === "" || CURRENCY_REG_EXP.test(e.target.value) || e.target.value.startsWith(',') || e.target.value.startsWith('.')) {
                                                setValue("discountAmount", checkValueWithInterval(e.target.value, 0, 1000000))
                                            }
                                        }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                control={control}
                                name="lmsList"
                                render={({field, fieldState: {error}}) => (
                                    <AdvancedDmsSelector key={lmsList} error={error} required={true}
                                                         disabled={lmsList.length === 0} value={field.value}
                                                         setValue={setValue} fieldName={"lmsList"} items={lmsList}
                                                         label={t('lms')} tenantId={watch('tenantId')}/>
                                )}/>
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                control={control}
                                name="dropDmsList"
                                render={({field, fieldState: {error}}) => (
                                    <AdvancedDmsSelector key={dropDmsList} error={error} required={true}
                                                         disabled={dropDmsList.length === 0} value={field.value}
                                                         setValue={setValue} fieldName={"dropDmsList"}
                                                         items={dropDmsList} label={t('dropDms')} tenantId={watch('tenantId')}/>
                                )}/>
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                control={control}
                                name="acrissCodeList"
                                render={({field, fieldState}) => (
                                        <TextField
                                            sx={{maxWidth:'50vw'}}
                                            fullWidth
                                            required
                                            disabled={acrisCodes.length === 0}
                                            label={t('acrissCode')}
                                            select
                                            value={field.value}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value[value.length - 1] === "all") {
                                                    setValue("acrissCodeList", field.value.length === acrisCodes.length ? [] : acrisCodes)
                                                } else {
                                                    field.onChange(e)
                                                }
                                            }}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            SelectProps={{
                                                multiple: true,
                                                renderValue: (selected: any) => selected.join(', '),
                                                MenuProps: MenuProps
                                            }}
                                        >
                                            <MenuItem value="all" className={"selectAll"}>
                                                <Checkbox
                                                    color={"accent"}
                                                    checked={isAllAcrissSelected}
                                                    indeterminate={
                                                        field.value.length > 0 && field.value.length < acrisCodes.length
                                                    }
                                                />
                                                <ListItemText className={"selectAllText"}
                                                              primary={t('selectAll')}/>
                                            </MenuItem>
                                            {acrisCodes?.map((a: any) => (
                                                <MenuItem key={a} value={a}>
                                                    <Checkbox color={"accent"}
                                                              checked={(field.value as string[]).indexOf(a) > -1}/>
                                                    <ListItemText primary={a}/>
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="freeDays"
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        label={t('freeDays')}
                                        type="number"
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="minimumDurationForFreeDays"
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        label={t('minDurationForFreeDays')}
                                        type="number"
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="minimumDuration"
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        label={t('minDuration')}
                                        type="number"
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                    </Grid>
                    <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                        <Grid item xs={12}>
                            <SubPeriods startDate={watch("pickupStartDate")}
                                        endDate={watch("pickupEndDate")}
                                        control={control}
                                        remove={remove}
                                        title={<>{t('subPeriods.title')}
                                            <Tooltip arrow sx={{float: "right", marginLeft: '16px'}}
                                                     title={t('subPeriods.tooltip')}>
                                                <InfoRounded color={"primary"}/>
                                            </Tooltip>
                                        </>}
                                        append={append}
                                        fields={fields}
                                        watch={watch}
                                        errors={formState.errors}
                                        fieldName={'exclusionSubperiods'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Accordion>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreRounded/>}
                                    aria-controls="ancillaries-content"
                                    id="ancillaries-header"
                                >
                                    <Typography>{t('ancillaryDiscount.title')}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Button
                                        disabled={ancillaryNameList.length === 0 || (ancillaries && ancillaries.some((s: any) => !s.name || (!s.discount && !s.amount)))}
                                        sx={{float: "right"}} variant={"text"} color={"accent"}
                                        endIcon={<LibraryAddRounded/>} onClick={addAncillary}>
                                        {t('ancillaryDiscount.add')}
                                    </Button>
                                    {ancillaries && ancillaries.length > 0 &&
                                        <Ancillaries ancillaries={ancillaries}
                                                     ancillaryNameList={ancillaryNameList}
                                                     setAncillaries={setAncillaries}
                                                     errorMessage={ancillariesErrorMessage}
                                                     setErrorMessage={setAncillariesErrorMessage}/>}
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>
                    <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                        <Grid container item xs={12} rowSpacing={1} columnSpacing={3} justifyContent={"flex-end"}>
                            <Grid item>
                                <Button variant={"outlined"} color={"accent"}
                                        onClick={() => {
                                            navigate("/salesmarketing/couponcampaignlist");
                                        }}>
                                    {t('buttons.cancel')}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button color={"accent"}
                                        type="submit">{t('buttons.confirm')}</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <CouponCampaignCreationModal isUpdate={isUpdate} open={openCreationModal}
                                         handleCloseCreationModal={() => setOpenCreationModal(false)}/>
        </>
    );
}

export default CouponCampaignCreateOrUpdatePage;